import { useEffect } from "react";
import {
  SegmentAnalyticsAdapter,
  useConsentGatedSegment,
} from "./useConsentGatedSegment";

interface SegmentUserAdapter {
  id: () => string | undefined | null;
}

export interface MerchantSegmentAnalyticsAdapter extends SegmentAnalyticsAdapter {
  identify: (userId: string) => Promise<unknown>;
  page: (path: string) => void;
  user: () => Promise<SegmentUserAdapter>;
}

interface UseMerchantSegmentAnalyticsOptions {
  writeKey: string;
  performanceAllowed: boolean;
  merchantId?: string;
  pathname?: string | null;
  load: (writeKey: string) => MerchantSegmentAnalyticsAdapter;
}

export const useMerchantSegmentAnalytics = <
  T extends MerchantSegmentAnalyticsAdapter,
>({
  writeKey,
  performanceAllowed,
  merchantId,
  pathname,
  load,
}: UseMerchantSegmentAnalyticsOptions): T | null => {
  const analytics = useConsentGatedSegment<T>({
    writeKey,
    performanceAllowed,
    load,
  });

  useEffect(() => {
    const identifyMerchant = async () => {
      if (!analytics) return;

      try {
        if (merchantId) {
          await analytics.identify(merchantId);
        } else {
          const user = await analytics.user();
          if (user.id()) {
            await analytics.reset();
          }
        }
      } catch {
        // don't care if this fails
      }
    };

    void identifyMerchant();
  }, [merchantId, analytics]);

  useEffect(() => {
    if (!analytics) return;

    analytics.page(pathname || "");
  }, [analytics, pathname]);

  return analytics;
};
